import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AppProvider } from './contexts/AppContext';
import theme from './styles/theme';

const YearsPage = lazy(() => import('./components/YearsPage'));
const FilieresPage = lazy(() => import('./components/FilieresPage'));
const ModulesPage = lazy(() => import('./components/ModulesPage'));
const ExamsPage = lazy(() => import('./components/ExamsPage'));

const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center',
    minHeight: '100vh',
    background: theme.colors.neutral.gray50,
  }}>
    <div style={{ textAlign: 'center' }}>
      <div className="spinner-border" style={{ color: theme.colors.primary.main }} role="status">
        <span className="visually-hidden">Chargement...</span>
      </div>
      <p style={{ 
        marginTop: '1rem',
        color: theme.colors.neutral.gray600,
        fontFamily: theme.typography.fontFamily.secondary,
      }}>
        Chargement de l'application...
      </p>
    </div>
  </div>
);

export default function App() {
  return (
    <HelmetProvider>
      <AppProvider>
        <Router>
        <div style={{ minHeight: '100vh' }}>
          <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap');
            @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=swap');
            
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            
            body {
              font-family: ${theme.typography.fontFamily.primary};
              overflow-x: hidden;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
            }
            
            .material-symbols-outlined {
              font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
              font-display: swap;
              user-select: none;
            }

            .spinner-border {
              width: 3rem;
              height: 3rem;
              border-width: 0.3em;
            }

            button {
              font-family: ${theme.typography.fontFamily.secondary};
            }

            /* Smooth scrolling */
            html {
              scroll-behavior: smooth;
            }

            /* Custom Scrollbar */
            ::-webkit-scrollbar {
              width: 10px;
              height: 10px;
            }

            ::-webkit-scrollbar-track {
              background: ${theme.colors.neutral.gray100};
            }

            ::-webkit-scrollbar-thumb {
              background: ${theme.colors.primary.main};
              border-radius: ${theme.borderRadius.full};
            }

            ::-webkit-scrollbar-thumb:hover {
              background: ${theme.colors.primary.dark};
            }

            /* Dark mode scrollbar */
            .dark-mode ::-webkit-scrollbar-track {
              background: ${theme.colors.dark.surface};
            }

            .dark-mode ::-webkit-scrollbar-thumb {
              background: ${theme.colors.primary.light};
            }

            /* Focus styles */
            button:focus-visible,
            a:focus-visible {
              outline: 2px solid ${theme.colors.primary.main};
              outline-offset: 2px;
            }

            /* Animation */
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateY(10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            .fade-in {
              animation: fadeIn 0.3s ease-out;
            }
          `}</style>

          <link
            href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
            rel="stylesheet"
          />

          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Home - Liste des années */}
              <Route path="/" element={<YearsPage />} />

              {/* Annees -> Filieres */}
              <Route path="/Annees/:yearId/Fillieres" element={<FilieresPage />} />

              {/* Filieres -> Modules (utilise le code de la filière) */}
              <Route path="/Annees/:yearId/Fillieres/:filiereCode/Modules" element={<ModulesPage />} />

              {/* Modules -> Resources (utilise le code du module) */}
              <Route path="/Annees/:yearId/Fillieres/:filiereCode/Modules/:moduleCode/Resources" element={<ExamsPage />} />

              {/* 404 - Redirection vers la page d'accueil */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </AppProvider>
    </HelmetProvider>
  );
}